/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-organisations.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { useAuthorization, useBoolean, useTranslation } from 'hooks';
import { SystemOrganisationFields } from 'queries/generated/fragments.generated';
import { GET_SYSTEM_ORGANISATIONS } from 'queries/org-admin';
import AddOrganizationDialog from 'views/components/admin/add-organization-dialog';
import AssignModulesToOrganisationDialog from 'views/components/admin/assign-modules-to-organisation';
import EditOrganizationDialog from 'views/components/admin/edit-organisation-dialog';
import EditOrganizationSecurityDialog from 'views/components/admin/edit-organisation-security-dialog';
import EditOrganizationConfDialog from 'views/components/admin/edit-organization-conf-dialog';
import OrganizationGrid from 'views/components/admin/organization-grid';
import Button from 'views/components/core/buttons/button';
import PageTitle from 'views/components/core/page-title/page-title';

const AdminOrganisations = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const { data, error, loading } = useQuery(GET_SYSTEM_ORGANISATIONS);
    const showAddOrganizationDialog = useBoolean(false);
    const showEditOrgCofDialog = useBoolean(false);
    const showAssignModuleDialog = useBoolean(false);
    const showEditSecurityDialog = useBoolean(false);
    const showEditOrganisationDialog = useBoolean(false);
    const [currentOrganization, setCurrentOrganization] = useState<SystemOrganisationFields | null>(null);
    const canEdit = user?.canWriteSystemAdminLevel();

    if (!user.systemAdminLevel) return null;

    // Use the new queries and mutations in queries/org-admin.ts to get and save all of the data for this page.
    // Also use GET_ORGANISATION (in uam.ts) to get the details for a single organisation

    const actions = (item: SystemOrganisationFields) => [
        {
            label: 'admin:editConfiguration',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentOrganization(item);
                showEditOrgCofDialog.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'admin:editOrganization',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentOrganization(item);
                showEditOrganisationDialog.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'admin:editSecurity',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentOrganization(item);
                showEditSecurityDialog.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'admin:assignModules',
            icon: ['fal', 'link'],
            onClick: () => {
                setCurrentOrganization(item);
                showAssignModuleDialog.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const openAddOrganizationDialog = () => {
        showAddOrganizationDialog.setTrue();
    };

    const organizations =
        data?.systemOrganisations?.map((organisation: any) => ({
            ...organisation,
            _mongoRegion: t(`admin:${organisation.config.mongoRegion}`),
            useTransaction: organisation.custom ? Boolean(organisation.custom?.useTransactions) : false,
            connectionString: organisation.custom?.connectionString ?? '',
            _createdByString: !organisation.createdBy
                ? ''
                : `${organisation.createdBy.firstName} ${organisation.createdBy.lastName}`,
            _updatedByString: !organisation.updatedBy
                ? ''
                : `${organisation.updatedBy.firstName} ${organisation.updatedBy.lastName}`,
        })) ?? [];

    return (
        <>
            <EditOrganizationDialog
                key={`EditOrganizationDialog:${currentOrganization?.key}`}
                currentOrganization={currentOrganization}
                open={showEditOrganisationDialog.value}
                close={() => {
                    setCurrentOrganization(null);
                    showEditOrganisationDialog.setFalse();
                }}
            />
            <EditOrganizationSecurityDialog
                currentOrganization={currentOrganization}
                open={showEditSecurityDialog.value}
                close={() => {
                    setCurrentOrganization(null);
                    showEditSecurityDialog.setFalse();
                }}
            />
            <EditOrganizationConfDialog
                key={`EditConfDialog:${currentOrganization?.key}`}
                currentOrganization={currentOrganization}
                open={showEditOrgCofDialog.value}
                organizations={organizations}
                close={() => {
                    setCurrentOrganization(null);
                    showEditOrgCofDialog.setFalse();
                }}
            />
            <AssignModulesToOrganisationDialog
                modulesAvailable={[]}
                currentOrganization={currentOrganization}
                open={showAssignModuleDialog.value}
                close={() => {
                    setCurrentOrganization(null);
                    showAssignModuleDialog.setFalse();
                }}
            />
            <AddOrganizationDialog
                open={showAddOrganizationDialog.value}
                organizations={organizations}
                close={() => {
                    showAddOrganizationDialog.setFalse();
                }}
            />
            <PageTitle text={t('admin:manageOrganization')} />
            <div className="btn-wrapper-manage-organization top-title-btn-wrapper">
                <Button
                    className="manage-organization-add-btn main-button"
                    text="admin:addOrganization"
                    disabled={!canEdit}
                    onClick={openAddOrganizationDialog}
                />
            </div>
            <OrganizationGrid data={organizations} actions={actions} />
        </>
    );
};

export default AdminOrganisations;
